import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { API_URL } from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Clock, 
  Plus, 
  Edit, 
  Trash2, 
  RefreshCw,
  Calendar,
  Coffee,
  AlertTriangle
} from 'lucide-react';

const API = API_URL;

export default function ShiftsPage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { effectiveTenantId, loading: tenantLoading } = useTenant();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    startTime: '08:00',
    endTime: '17:00',
    breakDuration: 30,
    toleranceLate: 5
  });

  const fetchShifts = useCallback(async () => {
    if (!effectiveTenantId) return;
    
    try {
      const response = await axios.get(`${API}/shifts?tenantId=${effectiveTenantId}`);
      setShifts(response.data);
    } catch (error) {
      console.error('Failed to fetch shifts:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  }, [effectiveTenantId, t]);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  const resetForm = () => {
    setFormData({
      name: '',
      startTime: '08:00',
      endTime: '17:00',
      breakDuration: 30,
      toleranceLate: 5
    });
    setEditingShift(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (shift) => {
    setEditingShift(shift);
    setFormData({
      name: shift.name,
      startTime: shift.startTime,
      endTime: shift.endTime,
      breakDuration: shift.breakDuration,
      toleranceLate: shift.toleranceLate
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (shift) => {
    setEditingShift(shift);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error(language === 'fr' ? 'Nom requis' : 'Name required');
      return;
    }

    if (!effectiveTenantId) {
      toast.error(language === 'fr' ? 'Tenant non sélectionné' : 'Tenant not selected');
      return;
    }

    try {
      if (editingShift) {
        await axios.put(`${API}/shifts/${editingShift.id}`, {
          name: formData.name,
          startTime: formData.startTime,
          endTime: formData.endTime,
          breakDuration: parseInt(formData.breakDuration),
          toleranceLate: parseInt(formData.toleranceLate)
        });
        toast.success(language === 'fr' ? 'Quart modifié' : 'Shift updated');
      } else {
        await axios.post(`${API}/shifts`, {
          tenantId: effectiveTenantId,
          name: formData.name,
          startTime: formData.startTime,
          endTime: formData.endTime,
          breakDuration: parseInt(formData.breakDuration),
          toleranceLate: parseInt(formData.toleranceLate)
        });
        toast.success(language === 'fr' ? 'Quart créé' : 'Shift created');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchShifts();
    } catch (error) {
      console.error('Shift error:', error.response?.data);
      // Handle Pydantic validation errors (array of objects) or string detail
      const errorData = error.response?.data?.detail;
      let message = t('error');
      if (typeof errorData === 'string') {
        message = errorData;
      } else if (Array.isArray(errorData)) {
        // Pydantic V2 validation errors
        message = errorData.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorData && typeof errorData === 'object') {
        message = errorData.msg || errorData.message || JSON.stringify(errorData);
      }
      toast.error(message);
    }
  };

  const handleDelete = async () => {
    if (!editingShift) return;

    try {
      await axios.delete(`${API}/shifts/${editingShift.id}`);
      toast.success(language === 'fr' ? 'Quart supprimé' : 'Shift deleted');
      setDeleteDialogOpen(false);
      setEditingShift(null);
      fetchShifts();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const calculateDuration = (start, end) => {
    try {
      const [startH, startM] = start.split(':').map(Number);
      const [endH, endM] = end.split(':').map(Number);
      const startMinutes = startH * 60 + startM;
      let endMinutes = endH * 60 + endM;
      // Handle overnight shifts (e.g., 22:00 - 06:00)
      if (endMinutes <= startMinutes) {
        endMinutes += 24 * 60; // Add 24 hours
      }
      const diff = endMinutes - startMinutes;
      const hours = Math.floor(diff / 60);
      const mins = diff % 60;
      return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
    } catch {
      return '-';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shifts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            {language === 'fr' ? 'Quarts de travail' : 'Work Shifts'}
          </h1>
          <p className="text-slate-500 mt-1">
            {shifts.length} {language === 'fr' ? 'quarts configurés' : 'shifts configured'}
          </p>
        </div>
        <Button onClick={openAddDialog} className="gap-2" data-testid="add-shift-btn">
          <Plus size={20} />
          {language === 'fr' ? 'Ajouter quart' : 'Add Shift'}
        </Button>
      </div>

      {/* Shifts Table */}
      <Card>
        <CardHeader className="border-b">
          <CardTitle className="font-heading flex items-center gap-2">
            <Clock size={20} />
            {language === 'fr' ? 'Liste des quarts' : 'Shift List'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-bold">{language === 'fr' ? 'Nom' : 'Name'}</TableHead>
                <TableHead className="font-bold">{language === 'fr' ? 'Début' : 'Start'}</TableHead>
                <TableHead className="font-bold">{language === 'fr' ? 'Fin' : 'End'}</TableHead>
                <TableHead className="font-bold">{language === 'fr' ? 'Durée' : 'Duration'}</TableHead>
                <TableHead className="font-bold">{language === 'fr' ? 'Pause' : 'Break'}</TableHead>
                <TableHead className="font-bold">{language === 'fr' ? 'Tolérance retard' : 'Late Tolerance'}</TableHead>
                <TableHead className="font-bold text-right">{language === 'fr' ? 'Actions' : 'Actions'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shifts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                    {language === 'fr' ? 'Aucun quart configuré' : 'No shifts configured'}
                    <br />
                    <span className="text-sm">
                      {language === 'fr' 
                        ? 'Créez un quart pour définir les horaires de travail'
                        : 'Create a shift to define work schedules'}
                    </span>
                  </TableCell>
                </TableRow>
              ) : (
                shifts.map((shift) => (
                  <TableRow key={shift.id} className="table-row-hover" data-testid={`shift-row-${shift.id}`}>
                    <TableCell className="font-medium">{shift.name}</TableCell>
                    <TableCell className="font-mono text-green-600">{shift.startTime}</TableCell>
                    <TableCell className="font-mono text-red-600">{shift.endTime}</TableCell>
                    <TableCell className="text-slate-600">{calculateDuration(shift.startTime, shift.endTime)}</TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-orange-600">
                        <Coffee size={14} />
                        {shift.breakDuration}min
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-amber-600">
                        <AlertTriangle size={14} />
                        {shift.toleranceLate}min
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(shift)}
                          data-testid={`edit-shift-${shift.id}`}
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => openDeleteDialog(shift)}
                          data-testid={`delete-shift-${shift.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Calendar className="text-blue-600 mt-0.5" size={20} />
            <div>
              <h4 className="font-medium text-blue-900">
                {language === 'fr' ? 'Comment utiliser les quarts?' : 'How to use shifts?'}
              </h4>
              <p className="text-sm text-blue-700 mt-1">
                {language === 'fr' 
                  ? 'Assignez un quart à chaque employé dans la page "Gestion employés". Le système calculera automatiquement les retards en fonction de l\'heure de début du quart.'
                  : 'Assign a shift to each employee in the "Employee Management" page. The system will automatically calculate late arrivals based on the shift start time.'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Clock size={20} />
              {editingShift 
                ? (language === 'fr' ? 'Modifier le quart' : 'Edit Shift')
                : (language === 'fr' ? 'Nouveau quart' : 'New Shift')
              }
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="shiftName">{language === 'fr' ? 'Nom du quart *' : 'Shift Name *'}</Label>
                <Input
                  id="shiftName"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={language === 'fr' ? 'Ex: Matin, Soir, Nuit...' : 'Ex: Morning, Evening, Night...'}
                  required
                  data-testid="shift-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startTime">{language === 'fr' ? 'Heure début *' : 'Start Time *'}</Label>
                  <Input
                    id="startTime"
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                    required
                    data-testid="shift-start-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endTime">{language === 'fr' ? 'Heure fin *' : 'End Time *'}</Label>
                  <Input
                    id="endTime"
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    required
                    data-testid="shift-end-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="breakDuration">{language === 'fr' ? 'Durée pause (min)' : 'Break Duration (min)'}</Label>
                  <Input
                    id="breakDuration"
                    type="number"
                    min="0"
                    max="120"
                    value={formData.breakDuration}
                    onChange={(e) => setFormData({ ...formData, breakDuration: e.target.value })}
                    data-testid="shift-break-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="toleranceLate">{language === 'fr' ? 'Tolérance retard (min)' : 'Late Tolerance (min)'}</Label>
                  <Input
                    id="toleranceLate"
                    type="number"
                    min="0"
                    max="60"
                    value={formData.toleranceLate}
                    onChange={(e) => setFormData({ ...formData, toleranceLate: e.target.value })}
                    data-testid="shift-tolerance-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" data-testid="save-shift-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">
              {language === 'fr' ? 'Supprimer le quart' : 'Delete Shift'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            {language === 'fr' 
              ? `Voulez-vous vraiment supprimer le quart "${editingShift?.name}" ? Les employés assignés à ce quart seront désassignés.`
              : `Are you sure you want to delete the shift "${editingShift?.name}"? Employees assigned to this shift will be unassigned.`
            }
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              type="button" 
              variant="destructive" 
              onClick={handleDelete}
              data-testid="confirm-delete-shift-btn"
            >
              {t('delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
